import camelcaseKeys from 'camelcase-keys'
import { SvelteDate, SvelteMap } from 'svelte/reactivity'
import { writable } from 'svelte/store'

import { handleFetch } from '@utils/fetch'

enum LlmSourceType {
  HUGGINGFACE_TRANSFORMERS = 'huggingface_transformers',
  LOCAL_TRANSFORMERS = 'local_transformers',
}

const llmSourceTypesValues = Object.values(LlmSourceType)
const displayLlmSourceTypes = {
  [LlmSourceType.HUGGINGFACE_TRANSFORMERS]: 'Transformers (HuggingFace)',
  [LlmSourceType.LOCAL_TRANSFORMERS]: 'Transformers (Local)',
}

class HuggingFaceTransformersSource {
  type = LlmSourceType.HUGGINGFACE_TRANSFORMERS

  public modelName = ''
  public token: string | null = null
  public tokenId: number | undefined = undefined
  public revision: string | null = null
  public codeRevision: string | null = null

  toJSON() {
    return {
      modelName: this.modelName,
      token: this.token ? this.token : null,
      tokenId: this.tokenId ? this.tokenId : undefined,
      revision: this.revision ? this.revision : null,
      codeRevision: this.codeRevision ? this.codeRevision : null,
    }
  }
}

class LocalTransformersSource {
  type = LlmSourceType.LOCAL_TRANSFORMERS

  public localPath = ''
  public parameterCount: number | null = null

  toJSON() {
    return {
      localPath: this.localPath,
      parameterCount: this.parameterCount,
    }
  }
}

type ILlmSources = HuggingFaceTransformersSource | LocalTransformersSource

const isHuggingFaceTransformersSource = (
  source: ILlmSources,
): source is HuggingFaceTransformersSource => {
  return source.type === LlmSourceType.HUGGINGFACE_TRANSFORMERS
}

const isLocalTransformersSource = (source: ILlmSources): source is LocalTransformersSource => {
  return source.type === LlmSourceType.LOCAL_TRANSFORMERS
}

interface ILlm {
  id: number

  organizationId?: number
  creatorId: number
  creatorName?: string
  createdAt: SvelteDate
  updatedAt: SvelteDate

  modelName: string
  modelSource: ILlmSources
}

type IRawLLM = Omit<ILlm, 'createdAt' | 'updatedAt'> & {
  createdAt: string
  updatedAt: string
}

interface ICreateLlm {
  organizationId?: number
  modelName: string
  modelSource: ILlmSources
  replaceIfExists?: boolean
  archiveExistingRuns?: boolean
}

const llmsStore = writable(new SvelteMap<number, ILlm>())

const parseLlm = (rawLlmModel: IRawLLM): ILlm => {
  const {
    id,
    organizationId,
    creatorId,
    creatorName,
    createdAt,
    updatedAt,
    modelName,
    modelSource,
  } = rawLlmModel

  return {
    id,
    organizationId,
    creatorId,
    creatorName,
    createdAt: new SvelteDate(SvelteDate.parse(createdAt)),
    updatedAt: new SvelteDate(SvelteDate.parse(updatedAt)),
    modelName,
    modelSource,
  }
}

const llmAsyncState = writable({
  loading: false,
  error: '',
})

const loadLlms = async () => {
  llmAsyncState.set({
    loading: true,
    error: '',
  })
  try {
    const llmModels: IRawLLM[] = camelcaseKeys(
      await (await handleFetch('/unlearning-llm/llm/')).json(),
      { deep: true },
    )
    const llmModelMap = llmModels.reduce((acc, llmModel) => {
      acc.set(llmModel.id, parseLlm(llmModel))
      return acc
    }, new SvelteMap<number, ILlm>())
    llmsStore.set(llmModelMap)
    llmAsyncState.set({
      loading: false,
      error: '',
    })
  } catch (err) {
    console.error('Failed to load models: ', err)
    llmAsyncState.set({
      loading: false,
      error: 'Failed to load models',
    })
  }
}

export type { ICreateLlm, ILlm }
export {
  displayLlmSourceTypes,
  HuggingFaceTransformersSource,
  isHuggingFaceTransformersSource,
  isLocalTransformersSource,
  llmAsyncState,
  LlmSourceType,
  llmSourceTypesValues,
  llmsStore,
  loadLlms,
  LocalTransformersSource,
  parseLlm,
}
